import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { LayoutDashboardIcon, ReceiptIcon, FileTextIcon, ScaleIcon, ShieldAlertIcon, MenuIcon, XIcon, PlaneIcon, LogOutIcon, ChevronDownIcon, SettingsIcon, UserIcon, SearchIcon } from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import { NotificationCenter } from '../features/NotificationCenter';
const adminNavItems = [{
  label: 'Dashboard',
  path: ROUTES.ADMIN,
  icon: LayoutDashboardIcon
}, {
  label: 'Refunds',
  path: ROUTES.ADMIN_REFUNDS,
  icon: ReceiptIcon
}, {
  label: 'Invoices',
  path: ROUTES.ADMIN_INVOICES,
  icon: FileTextIcon
}, {
  label: 'Reconciliation',
  path: ROUTES.ADMIN_RECONCILIATION,
  icon: ScaleIcon
}, {
  label: 'Fraud Detection',
  path: ROUTES.ADMIN_FRAUD,
  icon: ShieldAlertIcon
}];
// User dropdown menu
function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="relative" data-id="element-195">
      <motion.button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-surface-100 transition-colors" whileTap={{
      scale: 0.98
    }} data-id="element-196">
        <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20" data-id="element-197">
          <span className="text-body-sm font-bold text-white" data-id="element-198">AD</span>
        </div>
        <div className="text-left hidden xl:block" data-id="element-199">
          <p className="text-body-sm font-semibold text-surface-900" data-id="element-200">
            Admin User
          </p>
          <p className="text-caption text-surface-500" data-id="element-201">Administrator</p>
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-surface-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-202" />
      </motion.button>

      <AnimatePresence data-id="element-203">
        {isOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} data-id="element-204" />
            <motion.div initial={{
          opacity: 0,
          y: 10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.95
        }} transition={{
          duration: 0.15
        }} className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-surface-200 overflow-hidden z-50" data-id="element-205">
              <div className="p-2" data-id="element-206">
                <button onClick={() => {
              setIsOpen(false);
              // In a real app, navigate to profile page
              console.log('Navigate to profile');
            }} className="w-full flex items-center gap-3 px-3 py-2.5 text-body-sm text-surface-700 hover:bg-surface-50 rounded-lg transition-colors" data-id="element-207">
                  <UserIcon className="w-4 h-4" data-id="element-208" />
                  Profile
                </button>
                <button onClick={() => {
              setIsOpen(false);
              // In a real app, navigate to settings page
              console.log('Navigate to settings');
            }} className="w-full flex items-center gap-3 px-3 py-2.5 text-body-sm text-surface-700 hover:bg-surface-50 rounded-lg transition-colors" data-id="element-209">
                  <SettingsIcon className="w-4 h-4" data-id="element-210" />
                  Settings
                </button>
              </div>
              <div className="border-t border-surface-100 p-2" data-id="element-211">
                <Link to={ROUTES.HOME} onClick={() => setIsOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 text-body-sm text-error-600 hover:bg-error-50 rounded-lg transition-colors" data-id="element-212">
                  <LogOutIcon className="w-4 h-4" data-id="element-213" />
                  Sign Out
                </Link>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}
export function AdminLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const {
    scrollY
  } = useScroll();
  const headerShadow = useTransform(scrollY, [0, 50], ['0 0 0 rgba(0,0,0,0)', '0 4px 20px rgba(0,0,0,0.08)']);
  const isActive = (path: string) => location.pathname === path;
  return <div className="min-h-screen bg-gradient-to-br from-surface-100 via-surface-50 to-surface-100" data-id="element-214">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-surface-200" data-id="element-215">
        <div className="flex items-center justify-between h-16 px-4" data-id="element-216">
          <Link to={ROUTES.ADMIN} className="flex items-center gap-2" data-id="element-217">
            <motion.div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20" whileHover={{
            scale: 1.05,
            rotate: -5
          }} data-id="element-218">
              <PlaneIcon className="w-5 h-5 text-white" data-id="element-219" />
            </motion.div>
            <span className="text-heading-md font-bold text-surface-900" data-id="element-220">
              AeroZeep
            </span>
            <span className="text-caption font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md" data-id="element-221">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-2" data-id="element-222">
            <NotificationCenter data-id="element-223" />
            <motion.button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 text-surface-600 hover:bg-surface-100 rounded-lg" whileTap={{
            scale: 0.9
          }} data-id="element-224">
              <AnimatePresence mode="wait" data-id="element-225">
                {isSidebarOpen ? <motion.div key="close" initial={{
                rotate: -90,
                opacity: 0
              }} animate={{
                rotate: 0,
                opacity: 1
              }} exit={{
                rotate: 90,
                opacity: 0
              }} data-id="element-226">
                    <XIcon className="w-6 h-6" data-id="element-227" />
                  </motion.div> : <motion.div key="menu" initial={{
                rotate: 90,
                opacity: 0
              }} animate={{
                rotate: 0,
                opacity: 1
              }} exit={{
                rotate: -90,
                opacity: 0
              }} data-id="element-228">
                    <MenuIcon className="w-6 h-6" data-id="element-229" />
                  </motion.div>}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </header>

      <div className="flex" data-id="element-230">
        {/* Sidebar */}
        <aside className={`
            fixed lg:sticky top-0 left-0 z-30
            w-72 h-screen bg-white border-r border-surface-200
            transform transition-transform duration-300 ease-out lg:transform-none
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `} data-id="element-231">
          {/* Logo */}
          <div className="hidden lg:flex items-center gap-3 h-16 px-6 border-b border-surface-200" data-id="element-232">
            <Link to={ROUTES.ADMIN} className="flex items-center gap-3" data-id="element-233">
              <motion.div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20" whileHover={{
              scale: 1.05,
              rotate: -5
            }} data-id="element-234">
                <PlaneIcon className="w-6 h-6 text-white" data-id="element-235" />
              </motion.div>
              <div data-id="element-236">
                <span className="text-heading-md font-bold text-surface-900" data-id="element-237">
                  AeroZeep
                </span>
                <span className="ml-2 text-caption font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md" data-id="element-238">
                  Admin
                </span>
              </div>
            </Link>
          </div>

          {/* Search */}
          <div className="p-4 mt-16 lg:mt-0" data-id="element-239">
            <div className="relative" data-id="element-240">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" data-id="element-241" />
              <input type="text" placeholder="Search..." className="w-full pl-10 pr-4 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-body-sm placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all" onKeyDown={e => {
              if (e.key === 'Enter') {
                console.log('Search:', e.currentTarget.value);
              }
            }} data-id="element-242" />
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-caption text-surface-400 bg-surface-100 px-1.5 py-0.5 rounded" data-id="element-243">
                ⌘K
              </kbd>
            </div>
          </div>

          {/* Navigation */}
          <nav className="px-4 space-y-1" data-id="element-244">
            {adminNavItems.map((item, index) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return <motion.div key={item.path} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05
            }} data-id="element-245">
                  <Link to={item.path} onClick={() => setIsSidebarOpen(false)} className={`
                      relative flex items-center gap-3 px-4 py-3 rounded-xl text-body-md font-medium transition-all
                      ${active ? 'text-primary-600' : 'text-surface-600 hover:bg-surface-50 hover:text-surface-900'}
                    `} data-id="element-246">
                    {active && <motion.div layoutId="admin-nav-indicator" className="absolute inset-0 bg-primary-50 rounded-xl" transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} data-id="element-247" />}
                    <span className="relative z-10 flex items-center gap-3" data-id="element-248">
                      <Icon className="w-5 h-5" data-id="element-249" />
                      {item.label}
                    </span>
                    {item.label === 'Fraud Detection' && <span className="relative z-10 ml-auto w-5 h-5 bg-error-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-id="element-250">
                        3
                      </span>}
                  </Link>
                </motion.div>;
          })}
          </nav>

          {/* Bottom Actions */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-surface-200 bg-white" data-id="element-251">
            <Link to={ROUTES.HOME} className="flex items-center gap-3 px-4 py-3 rounded-xl text-body-md text-surface-600 hover:bg-surface-50 hover:text-surface-900 transition-colors" data-id="element-252">
              <LogOutIcon className="w-5 h-5" data-id="element-253" />
              Back to App
            </Link>
          </div>
        </aside>

        {/* Mobile Overlay */}
        <AnimatePresence data-id="element-254">
          {isSidebarOpen && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsSidebarOpen(false)} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-20 lg:hidden" data-id="element-255" />}
        </AnimatePresence>

        {/* Main Content */}
        <main className="flex-1 min-h-screen" data-id="element-256">
          {/* Top Bar */}
          <motion.div style={{
          boxShadow: headerShadow
        }} className="hidden lg:flex items-center justify-between h-16 px-8 bg-white/95 backdrop-blur-lg border-b border-surface-200 sticky top-0 z-20" data-id="element-257">
            <div className="flex items-center gap-4" data-id="element-258">
              <h1 className="text-heading-lg font-bold text-surface-900" data-id="element-259">
                {adminNavItems.find(item => isActive(item.path))?.label || 'Dashboard'}
              </h1>
            </div>
            <div className="flex items-center gap-4" data-id="element-260">
              {/* Notifications */}
              <NotificationCenter data-id="element-261" />

              {/* User Menu */}
              <UserMenu data-id="element-262" />
            </div>
          </motion.div>

          {/* Page Content */}
          <div className="p-4 lg:p-8" data-id="element-263">
            <Outlet data-id="element-264" />
          </div>
        </main>
      </div>
    </div>;
}